import type { IconComponent } from "reicon-react/createIcon";
import {
	Activity,
	ArrowSwapHorizontal,
	AtSign,
	BadgePercent,
	Bill,
	BookOpen,
	Briefcase,
	Buildings,
	Calendar,
	CalendarTick,
	Car,
	Card,
	CardTransfer,
	Cart,
	ChartBar,
	ChevronDown,
	ClipboardCheck,
	ClipboardList,
	Clock,
	CreditCard,
	Database,
	Document,
	DocumentText,
	Envelope,
	FileText,
	Folder,
	Forward,
	Gear,
	Globe,
	Grid,
	Handshake,
	HeartPulse,
	Home,
	Inbox,
	Invoice,
	Layers,
	Link,
	LockPassword,
	Money,
	MoneyBill,
	MoneySend,
	Note,
	Package,
	Phone,
	Plug,
	Profile,
	Receipt,
	Scale,
	Server,
	Settings,
	ShieldAlert,
	Share,
	Sms,
	Tag,
	Task,
	UserCircle,
	Users,
	Wallet,
	Window,
} from "reicon-react";
import { NavLink } from "react-router";

import { webmailInboxUrl } from "../lib/webmail.js";
import { MY_HUB_PAGES } from "../routes/shell/RecordPages";

const ICO = 16;

function Item({
	to,
	href,
	label,
	icon: Icon,
	end,
}: {
	to?: string;
	href?: string;
	label: string;
	icon: IconComponent;
	end?: boolean;
}) {
	if (href) {
		return (
			<a href={href} className="nav-link">
				<Icon size={ICO} aria-hidden="true" />
				<span>{label}</span>
			</a>
		);
	}
	return (
		<NavLink to={to!} end={end} className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}>
			<Icon size={ICO} aria-hidden="true" />
			<span>{label}</span>
		</NavLink>
	);
}

function Group({
	label,
	icon: Icon,
	pathname,
	items,
}: {
	label: string;
	icon: IconComponent;
	pathname: string;
	items: Array<{ to: string; label: string; icon: IconComponent }>;
}) {
	const open = items.some((item) => pathname === item.to || pathname.startsWith(`${item.to}/`));
	return (
		<details className="nav-drop" open={open}>
			<summary className="nav-link">
				<Icon size={ICO} aria-hidden="true" />
				<span>{label}</span>
				<ChevronDown size={12} aria-hidden="true" />
			</summary>
			<div className="nav-sub">
				{items.map((item) => (
					<Item key={item.to} to={item.to} label={item.label} icon={item.icon} />
				))}
			</div>
		</details>
	);
}

const MY_ICONS: Record<string, IconComponent> = {
	"my/tasks": Task,
	"my/profile": UserCircle,
	"my/claims": ClipboardList,
	"my/payslip": MoneyBill,
	"my/ea-form": FileText,
	"my/leave": CalendarTick,
	"my/attendance": Clock,
};

export function HubNav({
	pathname,
	viewAllTasks = true,
	showStaff = true,
}: {
	pathname: string;
	viewAllTasks?: boolean;
	showStaff?: boolean;
}) {
	return (
		<>
			<Group
				label="My"
				icon={Profile}
				pathname={pathname}
				items={MY_HUB_PAGES.map((page) => ({
					to: `/hub/${page.path}`,
					label: page.title,
					icon: MY_ICONS[page.path] ?? UserCircle,
				}))}
			/>
			{showStaff ? <Item to="/hub" label="Overview" icon={Home} end /> : null}
			{viewAllTasks ? <Item to="/hub/tasks" label="Task" icon={Task} /> : null}
			{showStaff ? (
				<>
			<Item to="/hub/calendar" label="Calendar" icon={Calendar} />
			<Item to="/hub/organisation" label="Organisation" icon={Buildings} />
			<Group
				label="Operation"
				icon={Briefcase}
				pathname={pathname}
				items={[
					{ to: "/hub/inventory", label: "Inventory", icon: Package },
					{ to: "/hub/consumables", label: "Consumables", icon: Layers },
					{ to: "/hub/customer-sites", label: "Site", icon: Buildings },
					{ to: "/hub/projects", label: "Project", icon: Folder },
					{ to: "/hub/work", label: "Work", icon: Briefcase },
				]}
			/>
			<Group
				label="Sales"
				icon={Handshake}
				pathname={pathname}
				items={[
					{ to: "/hub/leads", label: "Leads", icon: Users },
					{ to: "/hub/deals", label: "Deals", icon: Handshake },
					{ to: "/hub/items", label: "Item", icon: Tag },
					{ to: "/hub/quotations", label: "Quotation", icon: DocumentText },
					{ to: "/hub/sales-orders", label: "Sales order", icon: ClipboardList },
					{ to: "/hub/invoices", label: "Invoice", icon: Invoice },
					{ to: "/hub/receipts", label: "Receipts", icon: Receipt },
				]}
			/>
			<Group
				label="Purchases"
				icon={Cart}
				pathname={pathname}
				items={[
					{ to: "/hub/expenses", label: "Expenses", icon: MoneySend },
					{ to: "/hub/bills", label: "Bills", icon: Bill },
					{ to: "/hub/purchase-orders", label: "Purchase order", icon: Cart },
					{ to: "/hub/credits", label: "Credits", icon: Card },
				]}
			/>
			<Group
				label="People"
				icon={Users}
				pathname={pathname}
				items={[
					{ to: "/hub/leave", label: "Leave", icon: CalendarTick },
					{ to: "/hub/attendance", label: "Attendance", icon: Clock },
					{ to: "/hub/claims", label: "Claims", icon: ClipboardList },
					{ to: "/hub/payroll", label: "Payroll", icon: Money },
				]}
			/>
			<Group
				label="Finance"
				icon={Wallet}
				pathname={pathname}
				items={[
					{ to: "/hub/internal", label: "Internal", icon: Buildings },
					{ to: "/hub/transactions", label: "Transactions", icon: CardTransfer },
					{ to: "/hub/journals", label: "Journals", icon: BookOpen },
					{ to: "/hub/accounts", label: "Charts of accounts", icon: Scale },
					{ to: "/hub/tax", label: "Tax", icon: BadgePercent },
					{ to: "/hub/audit", label: "Audit", icon: ClipboardCheck },
					{ to: "/hub/assets", label: "Asset", icon: Buildings },
				]}
			/>
			<Group
				label="Admin"
				icon={Gear}
				pathname={pathname}
				items={[
					{ to: "/hub/maintenance", label: "Maintenance", icon: Settings },
					{ to: "/hub/letters", label: "Letters", icon: Sms },
					{ to: "/hub/repairs", label: "Repairs", icon: Gear },
					{ to: "/hub/register", label: "Register", icon: ClipboardList },
					{ to: "/hub/credentials", label: "Credentials", icon: LockPassword },
					{ to: "/hub/documents", label: "Documents", icon: Document },
					{ to: "/hub/vehicles", label: "Vehicle", icon: Car },
					{ to: "/hub/phones", label: "Phone number", icon: Phone },
				]}
			/>
			<Item to="/hub/reports" label="Reports" icon={ChartBar} />
			<Item to="/hub/activity" label="Activity logs" icon={Activity} />
			<Item to="/hub/integration" label="Integration" icon={Plug} />
				</>
			) : null}
		</>
	);
}

export function MailNav() {
	return (
		<>
			<Item to="/mail" label="Overview" icon={Home} end />
			<Item href={webmailInboxUrl()} label="Key Mail" icon={Envelope} />
			<Item to="/mail/mailboxes" label="Mailboxes" icon={Inbox} />
			<Item to="/mail/aliases" label="Aliases" icon={AtSign} />
			<Item to="/mail/forwarders" label="Forwarders" icon={Forward} />
			<Item to="/mail/migration" label="Migration" icon={ArrowSwapHorizontal} />
		</>
	);
}

export function SiteNav() {
	return (
		<>
			<Item to="/site" label="Overview" icon={Home} end />
			<Item to="/site/sites" label="Sites" icon={Window} />
			<Item to="/site/databases" label="Databases" icon={Database} />
			<Item to="/site/redirects" label="Redirects" icon={Link} />
		</>
	);
}

export function TeamNav() {
	return (
		<>
			<Item to="/team" label="Conversations" icon={Sms} />
		</>
	);
}

export function SocialNav() {
	return (
		<>
			<Item to="/social" label="Overview" icon={Home} end />
			<Item to="/social/channels" label="Channels" icon={Share} />
			<Item to="/social/calendar" label="Calendar" icon={Calendar} />
			<Item to="/social/compose" label="Compose" icon={Note} />
			<Item to="/social/results" label="Results" icon={ChartBar} />
		</>
	);
}

export function EngageNav() {
	return (
		<>
			<Item to="/engage/inbox" label="Inbox" icon={Inbox} />
			<Item to="/engage/accounts" label="Connect" icon={AtSign} />
			<Item to="/engage/policy" label="Policy" icon={Scale} />
		</>
	);
}

export function ManageNav({
	platformOwner,
	pathname,
	mailOnly = false,
}: {
	platformOwner: boolean;
	pathname: string;
	mailOnly?: boolean;
}) {
	if (mailOnly) {
		return (
			<>
				<Item href={webmailInboxUrl()} label="Key Mail" icon={Envelope} />
				<Item to="/manage/account/profile" label="Account" icon={Settings} />
			</>
		);
	}

	return (
		<>
			<Item to="/manage" label="Overview" icon={Home} end />
			<Item to="/manage/workspaces" label="Workspaces" icon={Grid} />
			<Item to="/manage/access" label="Access" icon={Users} />
			<Item to="/manage/domains" label="Domains" icon={Globe} />
			<Item to="/manage/billing" label="Billing" icon={CreditCard} />
			<Item to="/manage/config" label="Config" icon={Settings} />
			{platformOwner && (
				<details className="nav-drop" open={pathname.startsWith("/manage/platform")}>
					<summary className="nav-link">
						<Server size={ICO} aria-hidden="true" />
						<span>Platform</span>
						<ChevronDown size={12} aria-hidden="true" />
					</summary>
					<div className="nav-sub">
						<Group
							label="Mail"
							icon={Envelope}
							pathname={pathname}
							items={[
								{ to: "/manage/platform/mail/health", label: "Health", icon: HeartPulse },
								{ to: "/manage/platform/mail/queues", label: "Queues", icon: Layers },
								{ to: "/manage/platform/mail/abuse", label: "Abuse", icon: ShieldAlert },
								{ to: "/manage/platform/mail/server", label: "Server settings", icon: Server },
							]}
						/>
						<Item to="/manage/platform/audit" label="Audit" icon={ClipboardCheck} />
						<Group
							label="Caddy"
							icon={Globe}
							pathname={pathname}
							items={[
								{ to: "/manage/platform/caddy/routes", label: "Routes", icon: Globe },
								{ to: "/manage/platform/caddy/proxy", label: "Proxy", icon: Link },
								{ to: "/manage/platform/caddy/logs", label: "Logs", icon: Note },
								{ to: "/manage/platform/caddy/service", label: "Service", icon: Settings },
							]}
						/>
					</div>
				</details>
			)}
		</>
	);
}
